import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HeartHandshake, Scale, GraduationCap, Landmark, Upload, UserCheck, KeyRound, Undo2, Lock, FileText, BookOpen } from "lucide-react";
import { SiteNav } from "@/components/SiteNav";
import { Logo } from "@/components/Logo";

const BADGES = ["Delivered", "Verified", "Timestamped", "Audit Trail"];
const SECTORS = [
  { icon: HeartHandshake, name: "Care Providers", note: "Care plans & family notices" },
  { icon: Scale, name: "Legal Firms", note: "Client correspondence" },
  { icon: GraduationCap, name: "Universities", note: "Offers & student records" },
  { icon: Landmark, name: "Local Authorities", note: "Council & resident notices" },
];
const STEPS = [
  { icon: Upload, title: "Upload & pay", body: "Upload a PDF letter, add the recipient's name, email and postal address. £0.60 per send, VAT included." },
  { icon: UserCheck, title: "Recipient confirms identity", body: "They're notified by email and SMS — no attachment. To open it they must match their name and postal address." },
  { icon: KeyRound, title: "One-time code releases it", body: "A one-time code completes the claim. The letter appears in their mailbox to view or download." },
  { icon: Undo2, title: "Unclaimed? Returned.", body: "Reminders on day 7 and day 12. Unclaimed after 14 days, it's returned to you — timestamped." },
];

const Hero = () => (
  <section className="relative min-h-[88vh] flex items-end overflow-hidden grain" data-testid="hero-section">
    <img src="/img/hero.jpg" alt="Sealed letter on a desk at dusk" className="absolute inset-0 h-full w-full object-cover object-center" />
    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/10" />
    <div className="relative max-w-7xl mx-auto w-full px-6 pb-20 pt-32">
      <p className="rise text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400" style={{ animationDelay: "0.05s" }}>Verified digital mail · UK</p>
      <h1 data-testid="hero-headline" className="rise font-display text-white text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] mt-5 max-w-3xl" style={{ animationDelay: "0.15s" }}>
        Mail that proves<br />it arrived.
      </h1>
      <p data-testid="hero-subheadline" className="rise text-slate-200 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ animationDelay: "0.3s" }}>
        A secure, verified mailbox. Letters live in-app — never emailed as attachments. Recipients claim mail by confirming who they are, and every action is written to a tamper-evident audit trail.
      </p>
      <div className="rise flex flex-wrap gap-3 mt-9" style={{ animationDelay: "0.45s" }}>
        <Link to="/register" data-testid="hero-cta-primary" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors">
          Send your first letter <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </Link>
        <a href="#how" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 text-white font-semibold backdrop-blur-sm bg-white/5 hover:bg-white hover:text-slate-900 transition-colors">
          See how it works
        </a>
      </div>
      <div data-testid="status-badge-row" className="rise flex flex-wrap gap-2 mt-12" style={{ animationDelay: "0.6s" }}>
        {BADGES.map((b) => (
          <span key={b} data-testid={`hero-badge-${b.toLowerCase().replace(" ", "-")}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-400/30 backdrop-blur-md">
            {b} <CheckCircle2 size={14} />
          </span>
        ))}
      </div>
    </div>
  </section>
);

const TrustBar = () => (
  <section data-testid="sector-trust-bar" className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
    <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 dark:bg-slate-800 rounded-none">
      {SECTORS.map(({ icon: Icon, name, note }) => (
        <div key={name} className="bg-white dark:bg-slate-950 p-6 group">
          <Icon size={20} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
          <p className="font-display font-semibold mt-3">{name}</p>
          <p className="text-xs text-slate-500 mt-1">{note}</p>
        </div>
      ))}
    </div>
    <p className="text-center text-[11px] uppercase tracking-[0.25em] text-slate-400 pb-8">Built for the sectors where proof of delivery matters</p>
  </section>
);

const HowItWorks = () => (
  <section id="how" className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12">
    <div className="lg:col-span-4">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">The verified delivery flow</p>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">From upload to proof, in four steps.</h2>
      <p className="text-slate-600 dark:text-slate-400 mt-5 leading-relaxed">No attachments in inboxes, no guessing whether a letter was read. Every step is timestamped and chained into a hash-linked log.</p>
    </div>
    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
      {STEPS.map(({ icon: Icon, title, body }, i) => (
        <div key={title} data-testid={`how-step-${i + 1}`} className="rounded-xl border border-slate-200 dark:border-slate-800 p-7 bg-white dark:bg-slate-900 hover:-translate-y-1 hover:border-emerald-300 dark:hover:border-emerald-800 transition-[transform,border-color] duration-300">
          <div className="flex items-center justify-between">
            <Icon className="text-emerald-600" size={22} />
            <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
          </div>
          <h3 className="font-display text-lg font-semibold mt-5">{title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
  </section>
);

const AuditShowcase = () => (
  <section className="bg-slate-950 text-white relative overflow-hidden grain">
    <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">Tamper-evident audit trail</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">If a record changes, the chain breaks.</h2>
        <p className="text-slate-400 mt-5 leading-relaxed max-w-lg">Each event — upload, payment, notification, identity match, claim, open, return — is hashed with SHA-256 and linked to the one before it. Altering any entry is immediately detectable.</p>
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/80 backdrop-blur p-6 font-mono text-xs space-y-3" data-testid="audit-showcase">
        {[["LETTER_SENT", "9f2c…a71e", "0000…0000"], ["RECIPIENT_NOTIFIED", "4be0…19c3", "9f2c…a71e"], ["CLAIM_IDENTITY_MATCHED", "c81d…77f0", "4be0…19c3"], ["LETTER_CLAIMED", "e3a9…02bd", "c81d…77f0"]].map(([ev, h, p]) => (
          <div key={ev} className="flex items-center justify-between gap-4 border-b border-slate-800 pb-3 last:border-0">
            <span className="flex items-center gap-2 text-slate-200"><Lock size={12} className="text-emerald-400" />{ev}</span>
            <span className="text-emerald-400">{h}<span className="text-slate-600"> ← {p}</span></span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GuideTeaser = () => (
  <section data-testid="guide-teaser" className="max-w-7xl mx-auto px-6 pt-24">
    <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 p-8 sm:p-10 flex flex-wrap items-center justify-between gap-6">
      <div className="flex gap-5 items-start max-w-2xl">
        <span className="h-12 w-12 rounded-xl bg-emerald-600 text-white inline-flex items-center justify-center shrink-0"><BookOpen size={20} /></span>
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Step-by-step sending guide</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-2">Our full guide to sending single letters and bulk CSV batches is available to account holders. Create a free account to see it.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Link to="/register" data-testid="guide-teaser-register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors">Create an account <ArrowRight size={16} /></Link>
        <Link to="/app/guide" data-testid="guide-teaser-signin" className="text-sm font-semibold underline hover:text-emerald-600">Sign in to view</Link>
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10 items-center">
    <div className="lg:col-span-7">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Simple pricing</p>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Pay per letter. No subscription.</h2>
      <ul className="mt-6 space-y-3 text-sm text-slate-600 dark:text-slate-400">
        {["Identity-matched claim + one-time code", "Email + SMS notification (no attachment)", "14-day claim window with reminders", "Full timestamped tracking & audit trail"].map((t) => (
          <li key={t} className="flex gap-2 items-center"><CheckCircle2 size={16} className="text-emerald-600" />{t}</li>
        ))}
      </ul>
    </div>
    <div className="lg:col-span-5 rounded-2xl bg-slate-900 text-white p-10 dark:border dark:border-slate-800" data-testid="pricing-card">
      <FileText className="text-emerald-400" />
      <p className="font-display text-6xl font-extrabold mt-6">£0.60</p>
      <p className="text-slate-400 mt-1">per verified letter · GBP · VAT included</p>
      <Link to="/register" data-testid="pricing-cta" className="mt-8 inline-flex w-full justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 font-semibold hover:bg-emerald-500 transition-colors">Create free account <ArrowRight size={16} /></Link>
      <Link to="/enterprise" data-testid="pricing-enterprise-link" className="mt-4 block text-center text-sm text-slate-300 underline hover:text-white">Sending 10,000+ a month? Volume pricing from £0.45</Link>
    </div>
  </section>
);

export const SiteFooter = () => (
  <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
    <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row gap-6 justify-between">
      <div>
        <Logo />
        <p data-testid="footer-tagline" className="font-display text-lg mt-4">Verified. Timestamped. Delivered.</p>
      </div>
      <div className="text-sm text-slate-500 md:text-right">
        <p>postalcloud.app · <Link to="/enterprise" data-testid="footer-enterprise-link" className="underline hover:text-emerald-600">Enterprise & white-label</Link> · <Link to="/developers" data-testid="footer-developers-link" className="underline hover:text-emerald-600">Developers</Link> · <Link to="/verify" data-testid="footer-verify-link" className="underline hover:text-emerald-600">Verify a certificate</Link></p>
        <p className="mt-1 flex flex-wrap gap-x-3 md:justify-end">
          {[["terms", "Terms"], ["privacy", "Privacy"], ["gdpr", "GDPR"], ["cookies", "Cookies"]].map(([s, l]) => (
            <Link key={s} to={`/${s}`} data-testid={`footer-${s}-link`} className="underline hover:text-emerald-600">{l}</Link>
          ))}
        </p>
        <p className="mt-1">© {new Date().getFullYear()} PostalCloud. All rights reserved.</p>
        <p data-testid="footer-company-details" className="mt-3 text-xs text-slate-400 leading-relaxed">
          Pesnique Limited t/a PostalCloud · Registered in England & Wales, Company No. 10963497<br />
          4-6 Bridge Street, Annex 2 Wharfebank Mews, Tadcaster, North Yorkshire, LS24 9AL, UK
        </p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div>
      <SiteNav />
      <Hero />
      <TrustBar />
      <HowItWorks />
      <AuditShowcase />
      <GuideTeaser />
      <Pricing />
      <SiteFooter />
    </div>
  );
}
